// frontend/src/presentation/components/AppDebugPanel.tsx
// Widoczny na telefonie panel z checkpointami i logami.
// Pozwala zobaczyć w czasie rzeczywistym co się dzieje z Vosk, PTT, parserem, sync.

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getDebugLogEntries,
  subscribeDebugLog,
  clearDebugLog,
  exportDebugLog,
  type LogEntry,
} from '../../infrastructure/debug/AppDebugLog'

export function AppDebugPanel() {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<LogEntry[]>(getDebugLogEntries())
  const [filter, setFilter] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return subscribeDebugLog(() => {
      setEntries(getDebugLogEntries())
    })
  }, [])

  const handleExport = useCallback(() => {
    const text = exportDebugLog()
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text)
    }
    // Dla telefonu tworzymy również tymczasowy blob do udostępniania
    const blob = new Blob([text], { type: 'text/plain' })
    const file = new File([blob], `blackapp-debug-${Date.now()}.txt`, { type: 'text/plain' })
    if ((navigator as unknown as { share?: (data: unknown) => Promise<void> }).share) {
      void (navigator as unknown as { share: (data: unknown) => Promise<void> }).share({ files: [file] })
    }
  }, [])

  const filtered = entries.filter(e => {
    if (!filter) return true
    const q = filter.toLowerCase()
    return (
      e.tag.toLowerCase().includes(q) ||
      e.message.toLowerCase().includes(q) ||
      JSON.stringify(e.data).toLowerCase().includes(q)
    )
  })

  const levelColor: Record<string, string> = {
    checkpoint: '#22c55e',
    info: '#3b82f6',
    warn: '#f59e0b',
    error: '#ef4444',
    debug: '#9ca3af',
  }

  return (
    <div style={{ position: 'fixed', bottom: 120, right: 12, zIndex: 999999, fontFamily: 'monospace' }}>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: '2px solid #22c55e',
            background: '#111827',
            color: '#22c55e',
            fontSize: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            cursor: 'pointer',
          }}
          title="Debug checkpointi"
        >
          🐞
        </button>
      )}
      {open && (
        <div
          style={{
            width: '92vw',
            maxWidth: 520,
            height: '70vh',
            background: '#0f172a',
            color: '#e2e8f0',
            border: '1px solid #334155',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid #334155',
            }}
          >
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="filtruj tag/msg..."
              style={{
                flex: 1,
                background: '#1e293b',
                border: '1px solid #475569',
                borderRadius: 6,
                color: '#e2e8f0',
                padding: '6px 10px',
                fontSize: 13,
              }}
            />
            <button onClick={handleExport} style={buttonStyle}>Eksport</button>
            <button onClick={() => clearDebugLog()} style={buttonStyle}>Czyść</button>
            <button
              onClick={() => setOpen(false)}
              style={{ ...buttonStyle, background: '#ef4444', borderColor: '#dc2626', color: '#fff' }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
            {filtered.length === 0 && <div style={{ padding: 12, color: '#94a3b8' }}>Brak logów</div>}
            {filtered.map(e => (
              <div
                key={e.id}
                style={{
                  fontSize: 11,
                  lineHeight: 1.35,
                  marginBottom: 6,
                  paddingBottom: 6,
                  borderBottom: '1px solid #1e293b',
                }}
              >
                <span style={{ color: '#94a3b8' }}>
                  {new Date(e.timestamp).toISOString().slice(11, 23)}
                </span>{' '}
                <span style={{ color: levelColor[e.level] || '#fff', fontWeight: 700 }}>
                  {e.level.toUpperCase()}
                </span>{' '}
                <span style={{ color: '#60a5fa' }}>[{e.tag}]</span>{' '}
                {e.message}
                {e.data !== undefined && (
                  <pre style={{ margin: '4px 0 0', color: '#cbd5e1', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(e.data, null, 2)}
                  </pre>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        </div>
      )}
    </div>
  )
}

const buttonStyle: React.CSSProperties = {
  background: '#1e293b',
  border: '1px solid #475569',
  borderRadius: 6,
  color: '#e2e8f0',
  padding: '6px 10px',
  fontSize: 12,
  cursor: 'pointer',
}
